// --- React
import React, { useEffect } from 'react';
// --- Redux
import { useSelector } from 'react-redux';
// --- UI Libraries
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import { Col, Row } from 'antd';
// --- Components
import { InputLabel } from '../../../../components/InputLabel/InputLabel';
// --- Styles
import './styles.scss';
// --- API
import { datastageApi } from '../../../../api';
// --- Utilities
import { filterCriteria } from '../../../../utils/filterCriteria';
// --- Custom Hooks
import { useLoading } from '../../../../utils/hooks/useLoading';
import { useContactsActions } from '../../../../store/contacts/useContacts';

/**
 * ContactsFilterForm Component
 *
 * This component provides a form to filter and reset the contacts list.
 * It includes input fields and action buttons for filtering and resetting.
 *
 * @component
 * @returns {JSX.Element} The ContactsFilterForm component.
 */
export function ContactsFilterForm() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { updateContacts, updateContactsFilterForm, clearContacts } = useContactsActions();
    const { filterForm, defaultFilterRange, contactSort } = useSelector((s) => s.contacts);

    // Automatically apply the filter on component mount
    useEffect(() => {
        setTimeout(() => {
            handleFilter();
        }, 5);
    }, []);

    // Fetch and update contacts based on the current filter criteria
    const handleFilter = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.contacts(
                filterCriteria(filterForm, '', 'contacts'),
                defaultFilterRange,
                contactSort,
            );

            // Update store with the filtered contacts
            updateContacts({
                contacts: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
            });
        } catch (error) {
            console.error('Error filtering contacts:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Reset the filter and fetch all contacts
    const handleResetFilter = async () => {
        try {
            setLoadingTrue();

            // Clear the current filter form
            clearContacts();

            const data = await datastageApi.contacts({}, defaultFilterRange, contactSort);

            // Update store with all contacts
            updateContacts({
                contacts: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
            });
        } catch (error) {
            console.error('Error resetting contact filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <Row className="contacts-filter-form">
            <Col>
                {/* Input field for filtering contacts by name */}
                <InputLabel colProps={{ span: 24 }} label="Contact">
                    <Input
                        value={filterForm.name}
                        onChange={(e) =>
                            updateContactsFilterForm({
                                key: 'filterForm',
                                data: { name: e.target.value },
                            })
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleFilter();
                            }
                        }}
                        width="auto"
                        id="name"
                        name="name"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>

            <Col>
                {/* Action buttons for filtering and resetting */}
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
